/**
 * ⭐⭐ **O TÓPICO PROCESSOS JUDICIAIS RELEVANTES NO DESENHO DE IMÓVEIS — 19/09/2026.**
 *
 * O quarto da fila do pedido dele (PRD §46): resumo no alto, uma parte por vez,
 * os botões, a faixa do que foi pesquisado para a parte, uma linha por processo
 * que abre o detalhe — e, no detalhe, a nossa análise.
 *
 * ⛔ **Muda aparência e lugar, nunca o que o botão faz.** A busca no diário
 * (`ProcessosNoDjen`, grátis, de todos), o formulário de lançar e corrigir
 * (`Formulario`, o de antes — processo ainda não tem ficha passo a passo), a
 * análise (`AnaliseDoProcesso`, que confere ao gravar), o aceite, corrigir e
 * apagar são as mesmas peças — importadas, não copiadas.
 *
 * ⚠️ **O formulário de lançar deixou de ficar SEMPRE ABERTO** em cada parte: abre
 * num painel pelo botão "Lançar processo", como os de Imóveis. E a busca no
 * diário também abre pelo botão da faixa.
 */
import { useEffect, useState } from 'react';
import {
  apiInvestigados,
  apiTrilha,
  QUALIFICACOES,
  type AtivoDoInvestigado,
  type Investigado,
  type LinhaDaTrilha,
} from '../lib/api';
import { usarEhMaster } from '../lib/papel';
import { avisar, avisarErro } from './Avisos';
import { Confirmar } from './Confirmar';
import { AnaliseDoProcesso, Formulario, ProcessosNoDjen } from './AtivosLocalizados';
import { iniciais, Traco } from './ImoveisDoCaso';
import { dizer } from './TrilhaDaApuracao';
import '../estilos/imoveis-2026.css';

const emData = (d: string | null | undefined) => (d ? d.split('-').reverse().join('/') : '');
const minuscula = (t: string) => t.charAt(0).toLocaleLowerCase('pt-BR') + t.slice(1);
const PESQUISADA = new Set(['feito', 'nada_consta', 'a_confirmar']);

/** De onde vêm os processos — dito ao passar o mouse na marca da faixa. */
const FONTE_DO_DIARIO =
  'Diário de Justiça Eletrônico Nacional, do CNJ: sem custo, todos os tribunais, desde 2021. A busca é por nome — o ' +
  'Hub confere a identidade pelo que já sabe do caso e diz o quanto tem certeza.';

type Painel = null | 'lancar' | 'diario';

export function ProcessosDoCaso({ casoId, chave }: { casoId: string; chave: string }) {
  const ehMaster = usarEhMaster();
  const [partes, setPartes] = useState<Investigado[] | null>(null);
  const [porParte, setPorParte] = useState<Record<string, AtivoDoInvestigado[]>>({});
  const [trilha, setTrilha] = useState<Record<string, LinhaDaTrilha[]>>({});
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [revisao, setRevisao] = useState(0);
  const [painel, setPainel] = useState<Painel>(null);
  /* O processo em correção — o mesmo formulário de antes, preenchido. */
  const [corrigindo, setCorrigindo] = useState<AtivoDoInvestigado | null>(null);

  useEffect(() => {
    let vivo = true;
    void apiInvestigados
      .listar(casoId)
      .then((i) => {
        if (!vivo) return;
        setPartes(i.investigados);
        setEscolhida((atual) => atual ?? i.investigados[0]?.id ?? null);
      })
      .catch(() => vivo && setPartes([]));
    return () => {
      vivo = false;
    };
  }, [casoId, chave]);

  useEffect(() => {
    if (!partes) return;
    let vivo = true;
    void Promise.all(
      partes.map((p) =>
        apiInvestigados
          .ativos(p.id)
          .then((r) => [p.id, r.ativos.filter((a) => a.categoria === 'processos')] as const)
          .catch(() => [p.id, [] as AtivoDoInvestigado[]] as const),
      ),
    ).then((pares) => {
      if (vivo) setPorParte(Object.fromEntries(pares));
    });
    /* A faixa lê o estado que a trilha já calcula — só leitura, nada consulta. */
    void apiTrilha
      .doCaso(casoId)
      .then((t) => vivo && setTrilha(Object.fromEntries(t.partes.map((p) => [p.investigadoId, p.linhas]))))
      .catch(() => vivo && setTrilha({}));
    return () => {
      vivo = false;
    };
  }, [partes, revisao, casoId]);

  if (partes === null) return <p className="ajuda">Carregando…</p>;
  if (partes.length === 0) {
    return (
      <p className="ajuda ajuda-solta">
        Este caso ainda não tem parte investigada cadastrada. Os processos se lançam por pessoa — cadastre os
        investigados no caso primeiro.
      </p>
    );
  }

  const parte = partes.find((p) => p.id === escolhida) ?? partes[0]!;
  const processos = porParte[parte.id];
  const todos = Object.values(porParte).flat();
  const aoMudar = () => setRevisao((n) => n + 1);
  const linhaDe = (id: string) => (trilha[id] ?? []).find((l) => l.chave === 'processos');
  const diario = linhaDe(parte.id);
  const buscadas = partes.filter((p) => PESQUISADA.has(linhaDe(p.id)?.palavra ?? '')).length;
  const formularioAberto = painel === 'lancar' || corrigindo !== null;

  function abrir(p: Painel) {
    if (corrigindo) {
      avisar('Há uma correção aberta, com o que foi digitado. Grave ou cancele antes — nada foi descartado.');
      return;
    }
    setPainel(painel === p ? null : p);
  }

  return (
    <div className="imv">
      <Resumo processos={todos} buscadas={buscadas} partes={partes.length} />

      <div className="imv-pessoas" role="tablist" aria-label="Parte investigada">
        {partes.map((p) => {
          const n = porParte[p.id]?.length ?? 0;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={p.id === parte.id}
              className={p.id === parte.id ? 'ativo' : undefined}
              onClick={() => {
                if (formularioAberto) {
                  avisar('Lance ou cancele o formulário aberto antes de trocar de parte.');
                  return;
                }
                setEscolhida(p.id);
                setPainel(null);
              }}
            >
              <span className="imv-av">{iniciais(p.nome)}</span>
              <span className="imv-txt">
                <b>{p.nome}</b>
                <small>{QUALIFICACOES[p.qualificacao]}</small>
              </span>
              <span className={`imv-conta${n === 0 ? ' zero' : ''}`} title={n === 0 ? 'nada lançado' : undefined}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      <div className="imv-acoes">
        <button
          type="button"
          className="imv-bt primario"
          aria-expanded={painel === 'lancar'}
          onClick={() => abrir('lancar')}
        >
          <Traco d={['M12 5v14', 'M5 12h14']} />
          Lançar processo
        </button>
      </div>

      <div className="imv-fontes">
        <span className="imv-rot">Pesquisado para esta parte</span>
        <span className={`imv-fonte${PESQUISADA.has(diario?.palavra ?? '') ? ' ok' : ' falta'}`} title={FONTE_DO_DIARIO}>
          <i />
          Diário de Justiça · {diario ? minuscula(dizer(diario)) : 'em branco'}
        </span>
        <span className="imv-esp" />
        {/* ⚠️ Grátis, e por isso de todos — a régua da casa para consulta é o dinheiro. */}
        <button
          type="button"
          className={`imv-bt fantasma p${painel === 'diario' ? ' aberto' : ''}`}
          aria-expanded={painel === 'diario'}
          onClick={() => abrir('diario')}
        >
          <Traco d={['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16', 'm21 21-4.35-4.35']} />
          Buscar no diário
        </button>
      </div>

      {formularioAberto && (
        <div className="imv-painel">
          <p className="imv-painel-titulo">
            {corrigindo ? 'Corrigir processo' : 'Lançar processo'} · {parte.nome}
          </p>
          <Formulario
            key={corrigindo?.id ?? 'novo'}
            investigadoId={parte.id}
            categoria="processos"
            corrigindo={corrigindo}
            aoLancar={() => {
              setCorrigindo(null);
              setPainel(null);
              aoMudar();
            }}
            aoCancelar={() => {
              setCorrigindo(null);
              setPainel(null);
            }}
          />
        </div>
      )}
      {painel === 'diario' && (
        <div className="imv-painel">
          <ProcessosNoDjen investigadoId={parte.id} nome={parte.nome} ehMaster={ehMaster} aoMudar={aoMudar} />
        </div>
      )}

      <ListaDeProcessos
        processos={processos}
        investigadoId={parte.id}
        ehMaster={ehMaster}
        aoMudar={aoMudar}
        aoCorrigir={(a) => {
          if (formularioAberto) {
            avisar('Há um formulário aberto, com o que foi digitado. Lance ou cancele antes — nada foi descartado.');
            return;
          }
          setPainel(null);
          setCorrigindo(a);
        }}
      />
    </div>
  );
}

/* ── O resumo do caso ─────────────────────────────────────────────────────── */
function Resumo({ processos, buscadas, partes }: { processos: AtivoDoInvestigado[]; buscadas: number; partes: number }) {
  const comPosicao = processos.filter((a) => (a.situacao ?? '').trim()).length;
  const comAnalise = processos.filter((a) => (a.analise ?? '').trim()).length;
  return (
    <div className="imv-resumo">
      <div className="imv-kpi k-fora">
        <span className="imv-rot">Processos</span>
        <strong>{processos.length}</strong>
        <small>não somam ao patrimônio</small>
      </div>
      <div className="imv-kpi k-onus">
        <span className="imv-rot">Com posição</span>
        <strong>{comPosicao}</strong>
        <small>o papel da parte identificado</small>
      </div>
      <div className="imv-kpi k-livre">
        <span className="imv-rot">Com análise</span>
        <strong>{comAnalise}</strong>
        <small>escrita pela KAIRÓS</small>
      </div>
      <div className="imv-kpi k-sem">
        <span className="imv-rot">Partes buscadas</span>
        <strong>
          {buscadas} de {partes}
        </strong>
        <small>no diário de justiça</small>
      </div>
    </div>
  );
}

/* ── A lista ──────────────────────────────────────────────────────────────── */
function ListaDeProcessos({
  processos,
  investigadoId,
  ehMaster,
  aoMudar,
  aoCorrigir,
}: {
  processos: AtivoDoInvestigado[] | undefined;
  investigadoId: string;
  ehMaster: boolean;
  aoMudar: () => void;
  aoCorrigir: (a: AtivoDoInvestigado) => void;
}) {
  const [aberto, setAberto] = useState<string | null>(null);
  if (!processos) return <p className="ajuda">Carregando…</p>;
  const ordem = [...processos].sort((x, y) => (x.ocorridoEm ?? '9999').localeCompare(y.ocorridoEm ?? '9999'));
  return (
    <>
      <div className="imv-lista">
        <div className="imv-grade imv-cab">
          <span className="imv-rot">Posição</span>
          <span className="imv-rot">Processo</span>
          <span className="imv-rot">Distribuição</span>
          <span className="imv-rot dir">Nossa análise</span>
          <span />
        </div>
        {ordem.length === 0 ? (
          /* ⚠️ Vazio diz "nada lançado" e NÃO some: ausência de processo não é ausência de pesquisa. */
          <p className="imv-vazio">Nada lançado para esta parte.</p>
        ) : (
          ordem.map((a) => (
            <LinhaDoProcesso
              key={a.id}
              ativo={a}
              aberto={aberto === a.id}
              aoAbrir={() => setAberto(aberto === a.id ? null : a.id)}
              investigadoId={investigadoId}
              ehMaster={ehMaster}
              aoMudar={aoMudar}
              aoCorrigir={aoCorrigir}
            />
          ))
        )}
      </div>
      <div className="imv-legenda">
        <span className="imv-legenda-longa">
          <span className="imv-info">i</span> Os processos não somam ao patrimônio: o crédito que a parte disputa é
          expectativa, e a ação movida contra ela entra em Restrições e passivo
        </span>
      </div>
    </>
  );
}

function LinhaDoProcesso({
  ativo,
  aberto,
  aoAbrir,
  investigadoId,
  ehMaster,
  aoMudar,
  aoCorrigir,
}: {
  ativo: AtivoDoInvestigado;
  aberto: boolean;
  aoAbrir: () => void;
  investigadoId: string;
  ehMaster: boolean;
  aoMudar: () => void;
  aoCorrigir: (a: AtivoDoInvestigado) => void;
}) {
  const [aApagar, setAApagar] = useState(false);
  const [aMover, setAMover] = useState(false);
  /* A situação gravada diz o papel da parte e a instância — "EXECUTADO · 1º GRAU". */
  const partesDaSituacao = (ativo.situacao ?? '').split('·').map((x) => x.trim()).filter(Boolean);
  const ehInstancia = (x: string) => /\b(grau|inst[âa]ncia)\b|^(stj|stf|tst|tse|stm)\b/i.test(x);
  const instancia = partesDaSituacao.find(ehInstancia) ?? null;
  const posicao = partesDaSituacao.filter((x) => !ehInstancia(x)).join(' · ') || null;
  const temAnalise = !!(ativo.analise ?? '').trim();

  return (
    <article className={`imv-linha${aberto ? ' aberta' : ''}`}>
      <button type="button" className="imv-grade imv-linha-topo" onClick={aoAbrir} aria-expanded={aberto}>
        <span className="imv-sit-coluna">
          <span className={`imv-sit ${posicao ? 'onerado' : 'terceiro'}`}>
            <i />
            {posicao ? posicao.charAt(0) + posicao.slice(1).toLocaleLowerCase('pt-BR') : 'Não identificada'}
          </span>
        </span>
        <span className="imv-bem">
          <b>{ativo.descricao}</b>
          {ativo.identificacao && <span className="imv-mono">{ativo.identificacao}</span>}
          {/* ⭐ 19/09/2026: o que a busca no diário lançou sozinha diz de onde veio, e com que certeza. */}
          {ativo.origem?.startsWith('djen') && (
            <span className="imv-tag cinza do-diario">{ativo.origem === 'djen-provavel' ? 'diário · provável' : 'diário · confirmado'}</span>
          )}
        </span>
        <span className="imv-ato">
          <b>{emData(ativo.ocorridoEm) || '—'}</b>
          {instancia && <small>{instancia}</small>}
        </span>
        <span className="imv-valor">
          <b className={temAnalise ? undefined : 'na'}>{temAnalise ? 'escrita' : '—'}</b>
          <small>{temAnalise ? 'sai no relatório' : 'a escrever'}</small>
        </span>
        <span className="imv-chev" aria-hidden="true">
          <Traco d={[aberto ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6']} />
        </span>
      </button>

      {aberto && (
        <div className="imv-detalhe">
          <div>
            <h5 className="imv-rot">O processo</h5>
            <ul className="imv-mini">
              {ativo.identificacao && (
                <li>
                  <span>{ativo.identificacao}</span>
                </li>
              )}
              <li>
                <span>{ativo.ocorridoEm ? `Distribuído em ${emData(ativo.ocorridoEm)}` : 'Data de distribuição não apurada'}</span>
              </li>
              <li>
                <span>{posicao ? `Posição da parte: ${posicao}` : 'Posição da parte não identificada'}</span>
              </li>
              {instancia && (
                <li>
                  <span>{instancia}</span>
                </li>
              )}
            </ul>
          </div>
          {/* ⭐ A nossa análise, em largura inteira — a mesma peça de antes, que confere ao gravar. */}
          <div className="imv-detalhe-inteiro">
            <AnaliseDoProcesso ativo={ativo} investigadoId={investigadoId} aoMudar={aoMudar} />
          </div>

          {ativo.interpretacao && (
            <div className={`imv-interpretacao${ativo.aceita ? ' aceita' : ''}`}>
              <p>{ativo.interpretacao}</p>
              <p className="ajuda">
                {ativo.aceita ? 'Aceita — entra no documento entregue.' : 'Escrita pelo Hub, ainda não revista. Não entra no documento entregue.'}
                {ehMaster && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="botao-texto"
                      onClick={() =>
                        void (ativo.aceita
                          ? apiInvestigados.desfazerAceite(investigadoId, ativo.id)
                          : apiInvestigados.aceitarAtivo(investigadoId, ativo.id)
                        )
                          .then(() => {
                            aoMudar();
                            avisar(ativo.aceita ? 'Aceite desfeito.' : 'Aceita — entra no documento entregue.');
                          })
                          .catch(avisarErro)
                      }
                    >
                      {ativo.aceita ? 'desfazer o aceite' : 'aceitar'}
                    </button>
                  </>
                )}
              </p>
            </div>
          )}

          {ehMaster && (
            <div className="imv-detalhe-acoes">
              <button type="button" className="imv-bt p" onClick={() => aoCorrigir(ativo)}>
                <Traco d={['M4 20h4L19 9l-4-4L4 16z', 'M13.5 6.5l4 4']} />
                Corrigir
              </button>
              {/* ⭐ 07/10/2026, pedido da Karen: o processo que não importa ao caso vai para Restrições e passivo. */}
              <button type="button" className="imv-bt p fantasma" onClick={() => setAMover(true)}>
                <Traco d={['M5 12h14', 'm13 6 6 6-6 6']} />
                Irrelevante
              </button>
              <button type="button" className="imv-bt p fantasma perigo" onClick={() => setAApagar(true)}>
                <Traco d={['M4 7h16', 'M9 7V4.5h6V7', 'M6.5 7l1 13h9l1-13']} />
                Apagar
              </button>
            </div>
          )}
        </div>
      )}

      {aMover && (
        <Confirmar
          titulo="Mover para Restrições e passivo?"
          descricao={`"${ativo.descricao}" sai de Processos judiciais relevantes e entra em Restrições e passivo como ação judicial, sem valor documentado. O número, a data, a posição da parte${temAnalise ? ' e a nossa análise' : ''} vão junto, e a busca de processos não o traz de volta para cá.`}
          rotuloAcao="Mover"
          tom="primario"
          aoConfirmar={() =>
            void apiInvestigados
              .marcarIrrelevante(investigadoId, ativo.id)
              .then(() => {
                setAMover(false);
                aoMudar();
                avisar('Processo movido para Restrições e passivo.');
              })
              .catch(avisarErro)
          }
          aoFechar={() => setAMover(false)}
        />
      )}

      {aApagar && (
        <Confirmar
          titulo="Apagar este lançamento?"
          descricao={
            ativo.origem
              ? `"${ativo.descricao}" veio de consulta. Apagá-lo não devolve o que a consulta custou.`
              : `"${ativo.descricao}" será apagado.`
          }
          aoConfirmar={async () => {
            await apiInvestigados.apagarAtivo(investigadoId, ativo.id);
            setAApagar(false);
            aoMudar();
          }}
          aoFechar={() => setAApagar(false)}
        />
      )}
    </article>
  );
}
